import { StandardEvents } from '@shopify/events';
/** @typedef {import('@shopify/events').ProductSelectEvent} ProductSelectEvent */

/** Measurement items converted with the weight units; every other item uses the dimension units. */
const WEIGHT_ITEMS = ['weight', 'shipping_weight', 'shipping_weight_per_unit'];

/** Attributes copied from the new page HTML after a variant change. */
const STATE_ATTRIBUTES = ['data-dimension-units', 'data-weight-units', 'data-measurements-json'];

if (!customElements.get('ibd-measurements-component')) {
  customElements.define(
    'ibd-measurements-component',
    class IBDMeasurementsComponent extends HTMLElement {
      /** @type {string[]} The store's dimension unit, then the alternative */
      dimensionUnits = [];

      /** @type {string[]} The store's weight unit, then the alternative */
      weightUnits = [];

      /** @type {Record<string, any>} */
      measurements = {};

      /** @type {HTMLButtonElement|null} */
      toggleButton = null;

      /**
       * Whether the shopper switched to the alternative units. Kept across
       * variant changes, which render the store's units again.
       * @type {boolean}
       */
      showsAlternativeUnits = false;

      connectedCallback() {
        this.readState();
        this.addEventListener('click', this.handleClick);

        // The variant picker sits in another section, so its events never reach
        // this block's own section. See .ibd/references/ibd-product-weight.md
        document.addEventListener(StandardEvents.productSelect, this.handleProductSelect);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        document.removeEventListener(StandardEvents.productSelect, this.handleProductSelect);
      }

      /**
       * Reads the units, the measurements JSON and the toggle button from the
       * current markup, and disables the toggle when a value is missing.
       */
      readState() {
        this.dimensionUnits = (this.dataset.dimensionUnits ?? '').split(',');
        this.weightUnits = (this.dataset.weightUnits ?? '').split(',');
        this.toggleButton = this.querySelector('[data-toggle-unit]');

        try {
          this.measurements = JSON.parse(this.dataset.measurementsJson ?? '{}');
        } catch (error) {
          console.error(error);
          this.measurements = {};
        }

        if (!this.toggleButton) return;

        const missingItem = this.getMeasurementItems().find((item) => !this.getMeasurement(item, true));
        if (missingItem) {
          this.toggleButton.disabled = true;
          console.error(`IBD | Error: "${missingItem.dataset.measurementItem}" not found in measurements JSON`);
        }
      }

      /**
       * @returns {HTMLElement[]} The visible measurement items, the shipping weight included.
       */
      getMeasurementItems() {
        /** @type {NodeListOf<HTMLElement>} */
        const items = this.querySelectorAll('[data-measurement-item]');

        return Array.from(items).filter((item) => !item.hidden);
      }

      /**
       * @param {HTMLElement} item - A measurement item.
       * @param {boolean} alternative - Whether to read the alternative unit.
       * @returns {{ value: string|number, unit: string }|null} The value and unit label, or null when missing.
       */
      getMeasurement(item, alternative) {
        const measurementItem = item.dataset.measurementItem;
        if (!measurementItem) return null;

        const units = WEIGHT_ITEMS.includes(measurementItem) ? this.weightUnits : this.dimensionUnits;
        const measurements = this.measurements[units[alternative ? 1 : 0] ?? ''];
        const value = measurements?.[measurementItem];
        const unit = measurements?.['unit_label'];
        if (!value || !unit) return null;

        return { value, unit };
      }

      /**
       * Shows every visible measurement in the store's units or the alternative ones.
       * @param {boolean} alternative - Whether to show the alternative units.
       */
      showUnits(alternative) {
        for (const item of this.getMeasurementItems()) {
          const measurement = this.getMeasurement(item, alternative);

          /** @type {HTMLElement|null} */
          const valueEl = item.querySelector('[data-measurement-value]');

          /** @type {HTMLElement|null} */
          const unitEl = item.querySelector('[data-measurement-unit]');
          if (!measurement || !valueEl || !unitEl) continue;

          valueEl.textContent = String(measurement.value);
          unitEl.textContent = measurement.unit;
        }

        this.showsAlternativeUnits = alternative;

        const toggleButton = this.toggleButton;
        if (!toggleButton) return;

        // The button offers the units that are not shown
        const next = alternative ? 0 : 1;
        const newToggleButtonAriaLabel = toggleButton.hasAttribute('data-show-weight')
          ? `Convert to ${this.dimensionUnits[next]} and ${this.weightUnits[next]}`
          : `Convert to ${this.dimensionUnits[next]}`;
        toggleButton.setAttribute('aria-label', newToggleButtonAriaLabel);
      }

      /**
       * Delegated, because a variant change replaces the button.
       * @param {MouseEvent} event
       */
      handleClick = (event) => {
        if (!(event.target instanceof Element)) return;

        const toggleButton = event.target.closest('[data-toggle-unit]');
        if (!(toggleButton instanceof HTMLButtonElement) || toggleButton.disabled) return;

        this.showUnits(!this.showsAlternativeUnits);
      };

      /**
       * A variant change on the product page fetches the whole page: swap in this
       * block from it, since the shipping weight differs per variant. Product
       * cards and quick add render other products.
       * @param {ProductSelectEvent} event
       */
      handleProductSelect = (event) => {
        if (!(event.target instanceof Element)) return;
        if (event.target.closest('product-card, quick-add-dialog')) return;

        event.promise
          ?.then(({ detail }) => {
            if (!(detail?.html instanceof Document)) return;
            this.update(detail.html);
          })
          .catch((error) => {
            if (error?.name !== 'AbortError') console.warn('IBD | Measurements | Event promise rejected:', error);
          });
      };

      /**
       * Replaces the content and state attributes with this block's copy in the
       * new page HTML, then restores the shopper's choice of units.
       * @param {Document} html - The page HTML for the selected variant.
       */
      update(html) {
        if (!this.id) return;

        const newElement = html.getElementById(this.id);
        if (!newElement) return;

        // Import a copy: other listeners read the same document
        const fresh = /** @type {HTMLElement} */ (document.importNode(newElement, true));

        for (const name of STATE_ATTRIBUTES) {
          const value = fresh.getAttribute(name);
          if (value === null) {
            this.removeAttribute(name);
          } else {
            this.setAttribute(name, value);
          }
        }

        this.replaceChildren(...fresh.childNodes);
        this.readState();
        if (this.showsAlternativeUnits) this.showUnits(true);
      }
    }
  );
}
